import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { FixingMode } from "@/data/fixingMode";

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";

import {
  AutocompleteAsyncField,
  DatePickerField,
  InputField,
  SelectField,
  SelectFieldWithSection,
  TextareaField,
  TSections,
} from "@/features/FormCard/Fields";

import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";

import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";

import { FormActionsNew } from "@/features/FormCard/Actions";

import { useEditForm } from "@/features/Edit/hooks";
import { NewDiff } from "@/features/FormCard/NewDiff";
import useCurrencyOptions from "@/hooks/useCurrencyOption";
import { useEffect, useMemo, useRef } from "react";
import { useCategoryAutoSelection } from "./hook";
import { fieldTranslations } from "./translations";
import {
  deriveLocalCurrency,
  getKeyByTranslation,
  isCashAccount,
  isCashFormat,
  isSecuritiesAccount,
} from "./utils";
import { CashFeeContent, SecuritiesFeeContent } from "@/components/Form/FeesEdit";
import { computeFixingMode } from "../A5TimeSeries/utils";
import { AUTOMATIC_TAG_MAP } from "./config";
import i18n from "@/i18n";

const FormSchema = z.object({
  date: z.preprocess((val) => parseToDateValue(val as string), dateSchema),
  a5_portfolio_name: z.preprocess(
    preprocessTextAsyncAutocompleteField,
    z.union([
      // we recv / send as string the data
      z.string().optional().nullable(),

      // for the autocomplete, the form state is an object
      z.object({
        text: z.string(),
        id: z.number(),
        account_type: z.string(),
        currency: z.string(),
        reporting_currency: z.string(),
      }),
    ]),
  ),
  a5_static_datum_name: z.preprocess(
    preprocessTextAsyncAutocompleteField,
    z.union([
      // we recv / send as string the data
      z.string().optional().nullable(),

      // for the autocomplete, the form state is an object
      z.object({
        text: z.string(),
        id: z.number(),
        asset_class_2: z.string(),
        asset_class_1: z.string(),
        currency: z.string(),
        reporting_currency: z.string(),
      }),
    ]),
  ),
  a5_portfolio_reporting_currency: z.string().nullable().optional(),
  a5_static_datum_asset_class_2: z.string().nullable().optional(),
  a5_static_datum_asset_class_1: z.string().nullable().optional(),
  custodian_broker_fee: z.coerce.number().nullable().optional(),
  a5_portfolio_account_type: z.string().nullable().optional(),
  broker_fee: z.coerce.number().nullable().optional(),
  other_fee: z.coerce.number().nullable().optional(),
  fee_currency: z.string().nullable().optional(),
  description: z.string().optional().nullable(),
  fees: z.coerce.number().nullable().optional(),
  category: z.string().optional().nullable(),
  transfer: z.string().optional().nullable(),
  fee_type: z.string().nullable().optional(),
  market_value_ptf_ccy: z.coerce.number().nullable().optional(),
  market_value_local_ccy: z.coerce.number(),
  a5_static_datum_id: z.number(),
  a5_portfolio_id: z.number(),
  quantity: z.coerce.number().nullable().optional(),
  a5_portfolio_currency: z.string().nullable().optional(),
  a5_static_datum_currency: z.string().nullable().optional(),
});

type BaseForm = z.infer<typeof FormSchema>;

export type TForm = Omit<
  BaseForm,
  "a5_portfolio_name" | "a5_static_datum_name"
> & {
  a5_portfolio_name: Extract<BaseForm["a5_portfolio_name"], { id: number }>;
  a5_static_datum_name: Extract<
    BaseForm["a5_static_datum_name"],
    { id: number }
  >;
};

const client = "a5";
const table_name = "a5_transactions";

export function A5TransactionsNew() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    translations,
  } = useEditForm({
    client,
    table_name,
    FormSchema,
    fieldsNotApplyTranslation: ["transfer", "fee_type"],
    adjustOnReset: (key, value, fetchedData) => {
      if (key === "a5_portfolio_name") {
        return {
          text: value,
          id: fetchedData.a5_portfolio_id,
          account_type: fetchedData.a5_portfolio_account_type,
          currency: fetchedData.a5_portfolio_currency,
          reporting_currency: fetchedData.a5_portfolio_reporting_currency,
        };
      } else if (key === "a5_static_datum_name") {
        return {
          text: value,
          id: fetchedData.a5_static_datum_id,
          asset_class_2: fetchedData.a5_static_datum_asset_class_2,
          asset_class_1: fetchedData.a5_static_datum_asset_class_1,
          currency: fetchedData.a5_static_datum_currency,
        };
      }
      return value;
    },
    isNew: true,
  });

  const navigateToList = () => {
    navigate(-1);
  };

  const currencyOptions = useCurrencyOptions();

  const { category_options, isCategoryDisabled } = useCategoryAutoSelection({
    form,
    translations,
    keysAllowNullValue: ["fees"],
    isLoadingData: false,
  });


  const portfolio = form.watch("a5_portfolio_name");
  const portfolioId = portfolio?.id;
  const portfolioType = portfolio?.account_type;
  const portfolioCurrency = portfolio?.currency;
  const reportingCurrency = portfolio?.reporting_currency;

  const asset = form.watch("a5_static_datum_name");
  const assetCurrency = asset?.currency;
  const assetClass2 = asset?.asset_class_2 ?? null;

  const client_name = form.getValues("client")?.text ?? "";
  const bank_name = form.getValues("bank_name")?.text ?? "";

  const category = form.watch("category");
  const marketValueLocal = form.watch("market_value_local_ccy");
  const transferAutoRef = useRef(false);

  const localCurrency = useMemo(
    () =>
      deriveLocalCurrency(
        portfolioType ?? null,
        portfolioCurrency,
        assetCurrency,
      ),
    [portfolioType, portfolioCurrency, assetCurrency],
  );

  const isTypeCash = isCashAccount(portfolioType);
  const isTypeSecurities = isSecuritiesAccount(portfolioType);
  const isCategoryFees =
    getKeyByTranslation(category, translations?.["category"]) === "fees";
  const isCategoriesEmpty = category_options.options.length == 0;

  const isCashAsset = useMemo(() => isCashFormat(asset?.text), [asset]);

  const fixingMode: FixingMode | null = useMemo(
    () => computeFixingMode(assetClass2),
    [assetClass2],
  );

  const showQuantityField = isTypeSecurities && fixingMode === "fixing";

  const amountSign = useMemo(() => {
    const n = Number(marketValueLocal);
    return isNaN(n) ? 0 : n === 0 ? 0 : n > 0 ? 1 : -1;
  }, [marketValueLocal]);

  useEffect(() => {
    if (!AUTOMATIC_TAG_MAP.includes(assetClass2)) {
      if (transferAutoRef.current) {
        form.setValue("transfer", null, { shouldDirty: true });
        transferAutoRef.current = false;
      }
      return;
    }
    // compute the desired label from the sign
    const nextLabel =
      amountSign > 0 ? "deposit" : amountSign < 0 ? "withdrawal" : "";
    const current = form.getValues("transfer");

    // no label when amount is 0/blank: clear only if we set it
    if (!nextLabel) {
      if (transferAutoRef.current) {
        form.setValue("transfer", null, { shouldDirty: true });
        transferAutoRef.current = false;
      }
      return;
    }
    // update whenever the derived label differs
    if (current !== nextLabel) {
      form.setValue("transfer", nextLabel, { shouldDirty: true });
      transferAutoRef.current = true; // mark as auto-set
    }
  }, [assetClass2, marketValueLocal, form]);

  useEffect(() => {
    if (!showQuantityField) {
      form.setValue("quantity", null, { shouldDirty: true });
    }
  }, [showQuantityField]);

  return (
    <div className="grid place-items-center bg-gray-50 py-2 min-h-full">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full max-w-card">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t("transactions.new_transaction")}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name="date"
                    label={t("transactions.transaction_date")}
                    control={form.control}
                    isRequired
                  />
                </div>
                <AutocompleteAsyncField
                  name="a5_static_datum_name"
                  label={t("transactions.asset")}
                  control={form.control}
                  base_url="a5/a5_static_data"
                  id_column="a5_static_datum_id"
                  column_name="name"
                  isRequired
                  options={{
                    translateKey: "asset_class_2",
                    client: "a5",
                    table_name: "a5_static_data",
                    language: i18n.resolvedLanguage?.startsWith("fr")
                      ? "fr"
                      : "en",
                  }}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteAsyncField
                    name="client"
                    label={t("transactions.client")}
                    control={form.control}
                    base_url="a5/a5_portfolios"
                    id_column="a5_portfolio_id"
                    column_name="client"
                    showId={false}
                    isRequired
                    options={{
                      autoSelectSingle: true,
                      filters: () => {
                        const equals: Record<string, string> = {};
                        if (bank_name) equals.bank_name = bank_name;
                        if (portfolioId) equals.id = portfolioId;
                        return { equals };
                      },
                    }}
                  />
                  <AutocompleteAsyncField
                    name="bank_name"
                    label={t("transactions.bank")}
                    control={form.control}
                    base_url="a5/a5_portfolios"
                    id_column="a5_portfolio_id"
                    column_name="bank_name"
                    showId={false}
                    isRequired
                    options={{
                      autoSelectSingle: true,
                      filters: () => {
                        const equals: Record<string, string> = {};
                        if (client_name) equals.client = client_name;
                        if (portfolioId) equals.id = portfolioId;
                        return { equals };
                      },
                    }}
                  />
                </div>
                <AutocompleteAsyncField
                  name="a5_portfolio_name"
                  label={t("transactions.account")}
                  control={form.control}
                  base_url="a5/a5_portfolios"
                  id_column="a5_portfolio_id"
                  column_name="name"
                  isRequired
                  options={{
                    filters: () => {
                      const equals: Record<string, string> = {};
                      if (client_name) equals.client = client_name;
                      if (bank_name) equals.bank_name = bank_name;
                      return { equals };
                    },
                  }}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="market_value_local_ccy"
                    label={t("transactions.amount_local")}
                    type="number"
                    control={form.control}
                    isRequired
                    endContent={
                      <div className="opacity-50 text-xs">{localCurrency}</div>
                    }
                  />
                  <InputField
                    name="market_value_ptf_ccy"
                    label={t("transactions.amount_reporting")}
                    control={form.control}
                    type="number"
                    endContent={
                      <div className="opacity-50 text-xs">
                        {reportingCurrency}
                      </div>
                    }
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="quantity"
                    label={t("transactions.quantity")}
                    control={form.control}
                    type="number"
                    isRequired={showQuantityField}
                    fieldOptions={{
                      isDisabled: !showQuantityField,
                      changeOnDisable: false,
                    }}
                  />
                  <SelectField
                    name="transfer"
                    label={t("transactions.transfer_type")}
                    options={translations?.["transfer"]}
                    control={form.control}
                    allow_empty_option={true}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectFieldWithSection
                    label={t("transactions.movement_type")}
                    name="category"
                    sections={category_options.options as TSections}
                    control={form.control}
                    fieldOptions={{
                      isDisabled: isCategoryDisabled,
                      isVisible: !isCategoriesEmpty,
                    }}
                    allow_empty_option={isCashAsset}
                  />
                  <div className="overflow-hidden">
                    {isTypeCash && isCashAsset && (
                      <CashFeeContent
                        form={form}
                        isDisabled={!isCashAsset || !isCategoryFees}
                        feeOptionsTranslation={translations?.["fee_type"]}
                      />
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <TextareaField
                    name="description"
                    label={t("transactions.movement_description")}
                    control={form.control}
                  />
                </div>
                <div className="max-w-full bg-transparent text-xs">
                  {isTypeSecurities && (
                    <div className="py-2">
                      <Divider />
                      <SecuritiesFeeContent
                        form={form}
                        currencyOptions={currencyOptions}
                      />
                    </div>
                  )}
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_create")}
          children={
            changedFields
              ? NewDiff(
                  changedFields,
                  ["a5_static_datum_id", "a5_portfolio_id"],
                  fieldTranslations,
                )
              : t("no_changes")
          }
        />

        <CancelConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onCancel={navigateToList}
        />
      </div>
    </div>
  );
}
